<!--
  Usage:

  <script>
    let dialog = $state();
  </script>

  <button onclick={() => (dialog = { mode: 'edit' })}>Edit</button>

  {#if dialog?.mode === 'edit'}
    <Dialog onclose={() => dialog = undefined}>
      Modal content
    </Dialog>
  {/if}
-->
<script>
  import { nanoid } from '#lib/util.js';
  import { unique_classes } from '#lib/util.js';

  let {
    id = nanoid(),
    children,
    onclose,
    /** @type {'normal'|'wide'|'drawer'|string} variants + additional CSS classes for styling the dialog */
    class: css_class
  } = $props();

  let el = $state();

  let class_names = $derived(unique_classes(
    'dialog',
    'shadow-sm', 
    css_class
  ));


  function handle_close() {
    // Let the closing transition finish first.
    setTimeout(() => {
      onclose();
    }, 700);
  }

  $effect(() => {
    el.showModal();
  });
</script>

<!-- Forwards the onclose event to the caller -->
<dialog class={class_names} {id} bind:this={el} onclose={handle_close}>
  {@render children()}
</dialog>

<style lang='postcss'>
  /*   Open state of the dialog  */
  dialog[open] {
    opacity: 1;
  }

  :global(body:has(dialog[open]), html:has(dialog[open])) {
    /* TODO: This will also hide the dynamic island (due to its fixed position). 
    The overflow should be set specifcally on the main element in MainPageContent.svelte */
    /* overflow: hidden; */
  }

  /*   Closed state of the dialog   */
  /* less selector specificity */
  :where(dialog) {
    --dialog-max-height: calc(100svh - 2 * var(--dynamic-island-bottom-edge));
    max-height: var(--dialog-max-height);
  }
  dialog {
    border: none;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    position: fixed;

    /* Minimum safe padding that accounts for border-radius cropping.
       Dialog content can use var(--dialog-padding) for consistent spacing. */
    --dialog-padding: max(var(--space-m, 16px), var(--radius-lg, 20px));
    &:not(.px, .py, .ps, .pe, .px, .pbs, .pbe, .pis, .pie, .pyp) {
      padding: var(--dialog-padding);
    }
    /* Complex layout variants manage their own inner padding */
    &.drawer, &.wide, &.tag-dialog {
      &:not(.px, .py, .ps, .pe, .px, .pbs, .pbe, .pis, .pie, .pyp) {
        padding: 0;
      }
    }
    opacity: 0;
    transition:
      opacity 0.3s ease-out,
      transform 0.3s ease-out,
      overlay 0.3s ease-out allow-discrete,
      display 0.3s ease-out allow-discrete;
    background-color: var(--color-default-background);
    /* Equivalent to
    transition: all 0.3s allow-discrete; */
    border-radius: var(--radius-lg);
    &.normal {
      min-width: 242px;
    }
    &.wide {
      min-width: min(440px, calc(100vw - var(--size-4)));
    }
    &.drawer {
      overflow: hidden;
      :global(> *) {
        overflow-y: auto;
      }
      @container style(--lg-down: true) {
        max-width: calc(100vw - var(--scrollbar-width));
        max-height: 90svh;
        margin-block-end: 0;
        border-end-start-radius: 0;
        border-end-end-radius: 0;
      }
      @media (min-height: 900px) {
        margin-block-end: auto !important;
        border-end-start-radius: var(--radius-lg);
        border-end-end-radius: var(--radius-lg);
      }
    }
  }

  /*   Before-open state  */
  /* Needs to be after the previous dialog[open] rule to take effect,
      as the specificity is the same */
  @starting-style {
    dialog[open] {
      opacity: 0;
    }
  }

  /* Transition the :backdrop when the dialog modal is promoted to the top layer */
  dialog::backdrop {
    background-color: rgb(0 0 0 / 0%);
    transition:
      display 0.3s allow-discrete,
      overlay 0.3s allow-discrete,
      background-color 0.3s;
    /* Equivalent to
    transition: all 0.3s allow-discrete; */
  }

  dialog[open]::backdrop {
    background-color: rgb(0 0 0 / 25%);
    opacity: 1;
  }

  /* This starting-style rule cannot be nested inside the above selector
  because the nesting selector cannot represent pseudo-elements. */
  @starting-style {
    dialog[open]::backdrop {
      background-color: rgb(0 0 0 / 0%);
      opacity: 0;
    }
  }
</style>